import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_one } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';

import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';

const { DateTime } = require("luxon");

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

type Props = {
  object: User;
}

export const CoachUserWidgetsBasicIntake: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  // Pre-format dates as strings (Luxon, same output as <Dt />) so static +
  // value can live in ONE Typography string child, required for the html
  // attribute to engage (it only applies to single-string children).
  const fmtDay = (iso?: string) => {
    return !!iso ? DateTime.fromISO(iso).setZone("Europe/Brussels").toFormat("DDD", {locale: i18n.resolvedLanguage || 'nl'}) : '';
  }
  // const params = useParams();
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();

  // Coach-level widget settings: composite { cta } under one value key.
  // Stored on login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.basicIntakeValueKey];
  const showCta = HelpersClientWidgets.getBasicIntakeCtaEnabled(cfg);

  // Intake profile completion % (the file page's custom_action).
  const {
    data: intakeProgress,
    isSuccess: intakeProgressSuccess,
  } = useQuery({
    queryKey: ["intake_progress", object?.id, DateTime.now().toFormat("dd/LL/y")],
    queryFn: () =>
      fetch_one<{custom_result: number}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        {
          object: 'user',
          class_action: 'custom_api_get_intake_progress',
          id: object?.id,
        },
        login
      ),
    enabled: !!login && object?.type === "User::User" && !!object?.id,
  });

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row. Gated by
        the coach's `cta` setting. No premium lock, the file page's tabs gate
        their own modules. */}
    {showCta && <Button
      label={t("coach.general.actions.to_intake")}
      id="goto_intake"
      text
      size="small"
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}/file`, {state: {tab: 0}});
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.basic_intake")}</Typography>
        <Divider />
      </Grid>

      {/* No intake consent: the fixed warning. The body rows below render
          solely in the consented state. */}
      {!object.consent_status?.is_intake && <Grid item xs={12}>
        <Typography sx={{color: theme.palette.warning.main, fontSize: '0.7rem', fontStyle: 'italic'}} html>{t("coach.consents.no_access")}</Typography>
      </Grid>}

      {!!object.consent_status?.is_intake && <>
        {intakeProgressSuccess && <Grid item xs={12}>
          <Box sx={{position: 'relative'}}>
            <CircularProgress
              variant="determinate"
              value={intakeProgress?.custom_result || 0}
              size={64}
              thickness={5}
              sx={{color: theme.palette.primary.main, margin: 'auto', display: 'block'}}
            />
            <Typography variant="caption" sx={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}>
              {`${intakeProgress?.custom_result || 0}%`}
            </Typography>
          </Box>
        </Grid>}
        {!!object?.intake_profile?.updated_at && <Grid item xs={12}>
          <Typography variant="body2" html>{fmtDay(object?.intake_profile?.updated_at)}</Typography>
        </Grid>}
        {!!object?.intake_profile_practice?.updated_at && (object?.intake_profile_practice?.updated_at !== object?.intake_profile_practice?.created_at) && <Grid item xs={12}>
          <Typography variant="body2" html>{`${t("coach.submenu.intake_profiles.coach_time_updated_short")}: ${fmtDay(object?.intake_profile_practice?.updated_at)}${!!object?.intake_profile_practice?.editor_id ? ` ${t("general.terms.by")} ${object?.intake_profile_practice?.editor_full_name}` : ''}`}</Typography>
        </Grid>}
      </>}

    </Grid>
  </Paper>;
}
